/* CONTEXT:START
Wave: 2
Status: implemented
Stub-Type: external-api
Dependencies: [api-gateway-endpoint, authentication]
Risk-Level: medium
Validation-Method: integration-test
Performance-Target: <2000ms
Last-Updated: 2025-08-05
CONTEXT:END */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.example.com';

export interface User {
  id: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  userStatus?: string;
  enabled?: boolean;
}

export interface ListUsersResponse {
  users: User[];
  count: number;
  requestId: string;
}

class UserService {
  private tokenCache: { token: string; expiry: number } | null = null;

  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      // Check if we have a cached token that's still valid
      if (this.tokenCache && Date.now() < this.tokenCache.expiry) {
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.tokenCache.token}`,
        };
      }

      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Cache the token with a 5-minute buffer before expiry
      const expiry = (session.tokens?.idToken?.payload.exp || 0) * 1000 - 300000;
      this.tokenCache = { token, expiry };

      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Clear token cache on auth failure
          this.tokenCache = null;
          throw new Error('Authentication failed. Please log in again.');
        }
        
        if (response.status === 403) {
          throw new Error('Access denied. Admin privileges required.');
        }

        if (response.status === 429) {
          throw new Error('Too many requests. Please try again later.');
        }

        // Try to parse error response
        try {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
        } catch {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * List all Cognito users available for penholder assignment
   */
  async listUsers(): Promise<User[]> {
    try {
      const response = await this.makeRequest<ListUsersResponse>('/api/admin/users');
      return response.users || [];
    } catch (error) {
      console.error('Failed to fetch users:', error);
      
      // Provide user-friendly error messages
      if (error instanceof Error) {
        if (error.message.includes('Authentication failed')) {
          throw new Error('Please log in again to access user management.');
        }
        if (error.message.includes('Access denied')) {
          throw new Error('Admin privileges required to view users.');
        }
        if (error.message.includes('Too many requests')) {
          throw new Error('System is busy. Please try again in a moment.');
        }
        throw new Error(`Unable to load users: ${error.message}`);
      }
      
      throw new Error('Unable to load users. Please try again.');
    }
  }

  /**
   * Get user details by ID
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const users = await this.listUsers();
      return users.find(user => user.id === userId) || null;
    } catch (error) {
      console.error(`Failed to fetch user ${userId}:`, error);
      return null;
    }
  }

  /**
   * Search users by name or email
   */
  async searchUsers(query: string): Promise<User[]> {
    try {
      const users = await this.listUsers();
      const lowercaseQuery = query.toLowerCase();
      
      return users.filter(user => 
        user.name.toLowerCase().includes(lowercaseQuery) ||
        user.email.toLowerCase().includes(lowercaseQuery)
      );
    } catch (error) {
      console.error('Failed to search users:', error);
      throw error;
    }
  }

  /**
   * Clear authentication cache (useful for logout)
   */
  clearCache(): void {
    this.tokenCache = null;
  }
}

// Export singleton instance
export const userService = new UserService();
